import { Box, Typography, Button, Modal, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Switch, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useEffect, useState } from "react";
import { Toast } from 'primereact/toast';
import { useRef } from "react";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CustomAxios from "../../utils/CustomAxios";
import useStatusToggle from "../../hooks/useStatusToggle";
import StatusSwitch from "../../sub_components/status_switch";

export function BOCategorySettings() {
    const [categories, setCategories] = useState([]);
    const [businessOperations, setBusinessOperations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        category_name: '',
        business_operation_id: '',
        is_active: true
    });
    const [selectedId, setSelectedId] = useState(null);
    const statusToggle = useStatusToggle();
    const toast = useRef(null);

    useEffect(() => {
        fetchBusinessOperations();
        fetchCategories();
    }, []);

    const fetchBusinessOperations = async () => {
        try {
            const response = await CustomAxios.get('business-operations/');
            setBusinessOperations(response.data.filter(op => op.is_active));
        } catch (error) {
            showToast('error', 'Error', 'Failed to fetch business operations');
        }
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await CustomAxios.get('business-operations-categories/');
            setCategories(response.data);
        } catch (error) {
            showToast('error', 'Error', 'Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditMode(true);
            setSelectedId(item.id);
            setFormData({
                category_name: item.category_name,
                business_operation_id: item.business_operation,
                is_active: item.is_active
            });
        } else {
            setEditMode(false);
            setSelectedId(null);
            setFormData({
                category_name: '',
                business_operation_id: '',
                is_active: true
            });
        }
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditMode(false);
        setSelectedId(null);
        setFormData({
            category_name: '',
            business_operation_id: '',
            is_active: true
        });
    };

    const handleSubmit = async () => {
        if (!formData.category_name.trim()) {
            showToast('error', 'Validation Error', 'Category name is required');
            return;
        }
        if (!formData.business_operation_id) {
            showToast('error', 'Validation Error', 'Business operation is required');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                category_name: formData.category_name,
                business_operation_id: formData.business_operation_id,
                is_active: formData.is_active
            };
            if (editMode) {
                await CustomAxios.put(`business-operations-categories/${selectedId}/`, payload);
                showToast('success', 'Success', 'Category updated successfully');
            } else {
                await CustomAxios.post('business-operations-categories/create/', payload);
                showToast('success', 'Success', 'Category created successfully');
            }
            handleCloseModal();
            fetchCategories();
        } catch (error) {
            showToast('error', 'Error', error.response?.data?.error || 'Operation failed');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this category?')) {
            return;
        }

        try {
            setLoading(true);
            await CustomAxios.delete(`business-operations-categories/${id}/`);
            showToast('success', 'Success', 'Category deleted successfully');
            fetchCategories();
        } catch (error) {
            showToast('error', 'Error', error.response?.data?.error || 'Delete failed');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (item) => {
        const next = !item.is_active;
        await statusToggle.run(item.id, next, async () => {
            try {
                await CustomAxios.put(`business-operations-categories/${item.id}/`, {
                    category_name: item.category_name,
                    business_operation_id: item.business_operation,
                    is_active: next
                });
            } catch (error) {
                showToast('error', 'Error', 'Failed to update status');
                throw error;
            }
            showToast('success', 'Success', 'Status updated successfully');
            await fetchCategories();
        });
    };

    const showToast = (severity, summary, detail) => {
        if (toast.current) {
            toast.current.show({ severity, summary, detail, life: 3000 });
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Toast ref={toast} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">BO Category Settings</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal()}
                >
                    Add Category
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Category Name</TableCell>
                            <TableCell>Business Operation</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.category_name}</TableCell>
                                <TableCell>{item.business_operation_name}</TableCell>
                                <TableCell>
                                    <StatusSwitch
                                        checked={statusToggle.displayed(item.id, item.is_active)}
                                        pending={statusToggle.isPending(item.id)}
                                        onChange={() => handleToggleActive(item)}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => handleOpenModal(item)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(item.id)}>
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Modal open={modalOpen} onClose={handleCloseModal}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2
                }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {editMode ? 'Edit' : 'Add'} Category
                    </Typography>
                    <TextField
                        fullWidth
                        label="Category Name"
                        name="category_name"
                        value={formData.category_name}
                        onChange={(e) => setFormData({ ...formData, category_name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Business Operation</InputLabel>
                        <Select
                            value={formData.business_operation_id}
                            onChange={(e) => setFormData({ ...formData, business_operation_id: e.target.value })}
                            label="Business Operation"
                        >
                            {businessOperations.map((op) => (
                                <MenuItem key={op.id} value={op.id}>
                                    {op.operation_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ mr: 2 }}>Active Status:</Typography>
                        <Switch
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button onClick={handleCloseModal}>Cancel</Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                            {editMode ? 'Update' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}

